import React, { useMemo } from 'react';
import FieldMappingTable from './FieldMappingTable';
import { FieldMappings } from 'src/api/spreadsheet/workflow';
import { SelectDropdownOption } from 'src/components/common/SelectDropdown';
import useDivisions from 'src/api/organization/useDivisions';
import { useProductGroupsBySupplier } from 'src/api/rebates/useProductGroups';
import { WorkflowConstants } from '../types/WorkflowConstants';
import { NON_REBATABLE_PRODUCTS } from '@erp/shared-utils';

interface FieldMappingTableWithDataProps {
  mappings: FieldMappings;
  setMappings: React.Dispatch<React.SetStateAction<FieldMappings>>;
  constants: WorkflowConstants;
  supplierName?: string;
  disabled?: boolean;
}

/**
 * Higher-order component that provides API data to FieldMappingTable
 * This keeps the base FieldMappingTable component generic and reusable
 */
const FieldMappingTableWithData: React.FC<FieldMappingTableWithDataProps> = ({
  mappings,
  setMappings,
  constants,
  disabled = false,
}) => {
  // console.log('FieldMappingTableWithData');
  // Use supplier ID directly from constants; avoid searching by name here
  const supplierId = constants.supplierId as string;

  // Fetch divisions data
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch product groups for the selected supplier
  const {
    data: productGroupsData,
    isLoading: productGroupsLoading,
    error: _productGroupsError,
  } = useProductGroupsBySupplier(supplierId || '', !!supplierId);

  // Create dropdown options for divisions
  const divisionOptions: SelectDropdownOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];

    return divisionsData.divisions
      .filter((division) => division.name !== 'UNKNOWN') // Exclude UNKNOWN from options
      .map((division) => ({
        id: division.id,
        label: division.name,
        display: division.name,
        value: division,
      }));
  }, [divisionsData]);

  // Create dropdown options for product groups
  const productGroupOptions: SelectDropdownOption[] = useMemo(() => {
    if (!productGroupsData) return [];

    // Product groups from API (includes the catch-all group with isCatchAll=true)
    const regularOptions = productGroupsData
      .filter((group) => group.name !== 'UNKNOWN') // Exclude UNKNOWN from options
      .map((group) => ({
        id: group.id,
        label: group.isCatchAll
          ? `${group.name} (catch-all)${group.description ? ` - ${group.description}` : ''}`
          : `${group.name}${group.description ? ` - ${group.description}` : ''}`,
        display: group.name,
        value: group,
      }));

    // Non-rebatable is the only virtual option (maps to null in database)
    const nonRebatableOption: SelectDropdownOption = {
      id: 'non-rebatable-products',
      label: 'Non Rebatable Products (excluded from rebate calculations)',
      display: NON_REBATABLE_PRODUCTS,
      value: { id: null, name: NON_REBATABLE_PRODUCTS },
    };

    return [nonRebatableOption, ...regularOptions];
  }, [productGroupsData]);

  // Configure mapping types with fetched data
  const mappingTypeConfigs = useMemo(
    () => ({
      divisionMappings: {
        displayName: 'Division Mappings',
        options: divisionOptions,
        isLoading: divisionsLoading,
        placeholder: 'Select division...',
      },
      productMappings: {
        displayName: 'Product Mappings',
        options: productGroupOptions,
        isLoading: productGroupsLoading,
        placeholder: supplierId ? 'Select product group...' : 'No supplier set',
      },
      dateMappings: {
        displayName: 'Date Mappings',
        placeholder: 'Pick a date',
        // No dropdown options = date picker
      },
    }),
    [
      divisionOptions,
      productGroupOptions,
      divisionsLoading,
      productGroupsLoading,
      supplierId,
    ],
  );

  return (
    <FieldMappingTable
      mappings={mappings}
      setMappings={setMappings}
      mappingTypeConfigs={mappingTypeConfigs}
      disabled={disabled}
    />
  );
};

export default FieldMappingTableWithData;
